import { useCallback, useEffect, useRef, useState } from 'react';
import type { CanvasGraph, CanvasNode, CanvasSelection } from '../types';

export interface UseCanvasSelectionOptions {
  canvasGraph: CanvasGraph;
  isGraphRefreshing: boolean;
}

export function useCanvasSelection({ canvasGraph, isGraphRefreshing }: UseCanvasSelectionOptions) {
  const [selection, setSelection] = useState<CanvasSelection | null>(null);
  const lastStableSelectionRef = useRef<CanvasSelection | null>(null);
  const isGraphRefreshingRef = useRef<boolean>(false);
  const [pendingCreateSelection, setPendingCreateSelection] = useState<{
    targetTable: string;
    targetField: string;
  } | null>(null);
  const suppressNullSelectionUntilRef = useRef<number>(0);

  // Keep a ref to canvasGraph so handleSelectionChange doesn't need it as a dependency
  const canvasGraphRef = useRef(canvasGraph);
  canvasGraphRef.current = canvasGraph;

  const areSelectionsEqual = useCallback(
    (left: CanvasSelection | null, right: CanvasSelection | null): boolean => {
      if (!left && !right) return true;
      if (!left || !right) return false;
      if (left.kind !== right.kind) return false;
      if (left.kind === 'node' && right.kind === 'node') return left.node.id === right.node.id;
      if (left.kind === 'edge' && right.kind === 'edge') return left.edge.id === right.edge.id;
      return false;
    },
    [],
  );

  const handleSelectionChange = useCallback(
    (nextSelection: CanvasSelection | null) => {
      const isTableFieldSelection =
        nextSelection?.kind === 'node' && nextSelection.node.type === 'targetField';

      // When ReactFlow fires a null selection (e.g. after a graph re-render
      // triggered by auto-fetch preview, density change, etc.), check whether
      // the previously selected node/edge still exists in the current graph.
      // If it does, the deselection is spurious — suppress it.
      if (!nextSelection && lastStableSelectionRef.current) {
        // Always suppress within the timing window
        if (suppressNullSelectionUntilRef.current > Date.now()) {
          return;
        }
        // Also suppress if the selected element still exists in the graph
        const stable = lastStableSelectionRef.current;
        const cg = canvasGraphRef.current;
        const stillExists =
          stable.kind === 'node'
            ? cg?.nodes.some((n) => n.id === stable.node.id)
            : cg?.edges.some((e) => e.id === stable.edge.id);
        if (stillExists) {
          return;
        }
      }

      setSelection((previous) => {
        if (!isTableFieldSelection && areSelectionsEqual(previous, nextSelection)) return previous;
        return nextSelection;
      });

      if (!isTableFieldSelection && areSelectionsEqual(lastStableSelectionRef.current, nextSelection)) return;

      if (nextSelection) {
        lastStableSelectionRef.current = nextSelection;
        // Suppress null selections for 500ms after a positive selection
        suppressNullSelectionUntilRef.current = Date.now() + 500;
        return;
      }

      if (!isGraphRefreshingRef.current) {
        lastStableSelectionRef.current = null;
      }
    },
    [areSelectionsEqual],
  );

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: CanvasNode) => {
      handleSelectionChange({ kind: 'node', node });
    },
    [handleSelectionChange],
  );

  const handleOverviewSelectEdge = useCallback(
    (edgeId: string, setFocusNodeId: (id: string) => void) => {
      const cg = canvasGraphRef.current;
      if (!cg) return;
      const edge = cg.edges.find((e) => e.id === edgeId);
      if (edge) {
        handleSelectionChange({ kind: 'edge', edge });
        if (edge.target) {
          setFocusNodeId(edge.target);
        }
      }
    },
    [handleSelectionChange],
  );

  // Track graph refreshing state
  useEffect(() => {
    isGraphRefreshingRef.current = isGraphRefreshing;
  }, [isGraphRefreshing]);

  // Re-select after graph refresh
  useEffect(() => {
    if (selection || isGraphRefreshing) return;
    const pendingSelection = lastStableSelectionRef.current;
    if (!pendingSelection) return;
    const exists =
      pendingSelection.kind === 'node'
        ? canvasGraph?.nodes.some((node) => node.id === pendingSelection.node.id)
        : canvasGraph?.edges.some((edge) => edge.id === pendingSelection.edge.id);
    if (exists) {
      setSelection((previous) => {
        if (areSelectionsEqual(previous, pendingSelection)) return previous;
        return pendingSelection;
      });
    } else {
      lastStableSelectionRef.current = null;
    }
  }, [selection, isGraphRefreshing, canvasGraph, areSelectionsEqual]);

  // Re-select after Create Mapping
  useEffect(() => {
    if (!pendingCreateSelection || !canvasGraph) return;
    const needle =
      `${pendingCreateSelection.targetTable}.${pendingCreateSelection.targetField}`.toLowerCase();
    const matchingEdge = canvasGraph.edges.find((e) => e.target?.toLowerCase().includes(needle));
    if (matchingEdge) {
      setPendingCreateSelection(null);
      const sel: CanvasSelection = { kind: 'edge', edge: matchingEdge };
      lastStableSelectionRef.current = sel;
      setSelection(sel);
      suppressNullSelectionUntilRef.current = Date.now() + 500;
    } else {
      setPendingCreateSelection(null);
    }
  }, [pendingCreateSelection, canvasGraph]);

  // Clear inspector status on selection change (handled by consumer)
  const clearSelection = useCallback(() => {
    lastStableSelectionRef.current = null;
    setSelection(null);
  }, []);

  return {
    selection,
    setSelection,
    lastStableSelectionRef,
    pendingCreateSelection,
    setPendingCreateSelection,
    suppressNullSelectionUntilRef,
    handleSelectionChange,
    handleNodeClick,
    handleOverviewSelectEdge,
    clearSelection,
    areSelectionsEqual,
  };
}
